import React, { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FileSpreadsheet, Download, Upload, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const tzs = (v) => (v === null || v === undefined || v === "" ? "—" : `TZS ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

export const saveBlob = (res, fallback) => {
  const cd = res.headers?.["content-disposition"] || "";
  const name = (cd.match(/filename="?([^"]+)"?/) || [])[1] || fallback;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

function PreviewTable({ p }) {
  return (
    <div className="max-h-[45vh] overflow-auto rounded-md border border-border">
      <table className="w-full text-xs" data-testid="excel-preview-table">
        <thead className="sticky top-0 bg-slate-50 text-left text-slate-500"><tr><th className="px-3 py-2">Item</th><th className="px-3 py-2 text-right">Current</th><th className="px-3 py-2 text-right">From sheet</th></tr></thead>
        <tbody>
          {p.matched.map((m) => (
            <tr key={m.item_id} className="border-t border-border" data-testid={`excel-row-${m.item_id}`}>
              <td className="px-3 py-2 text-slate-700">{m.description}</td>
              <td className="px-3 py-2 text-right font-mono text-slate-400">{tzs(m.current)}</td>
              <td className="px-3 py-2 text-right font-mono font-semibold text-[#1B4383]">{tzs(m.landed)}</td>
            </tr>
          ))}
          {p.errors.map((e) => (
            <tr key={e.item_id} className="border-t border-border bg-red-50"><td className="px-3 py-2 text-red-700">{e.description}</td><td colSpan={2} className="px-3 py-2 text-right text-red-700">{e.error}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const ExcelCostingOverride = ({ rfqId, supplierId, onApplied }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const base = `/rfqs/${rfqId}/suppliers/${supplierId}/costing`;

  const download = async () => {
    setBusy("template");
    try { saveBlob(await api.get(`${base}/excel-template`, { responseType: "blob" }), "costing-template.xlsx"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const pick = async (file, sheet) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5 MB"); return; }
    setBusy("preview");
    try {
      const b64 = typeof file === "string" ? file : await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const filename = typeof file === "string" ? preview.filename : file.name;
      const { data } = await api.post(`${base}/excel-preview`, { file: b64, filename, sheet });
      setPreview({ ...data, file: b64, filename });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const apply = async () => {
    setBusy("apply");
    try {
      const { data } = await api.post(`${base}/excel-apply`, { file: preview.file, filename: preview.filename, sheet: preview.sheet });
      toast.success(`Excel costing applied to ${data.applied} item(s). The Somcore Quote now uses these landed costs.`);
      setPreview(null); onApplied(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <>
      <div className="mr-auto flex flex-wrap items-center gap-2" data-testid="excel-costing">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500"><FileSpreadsheet className="h-4 w-4 text-[#2F6B45]" />Excel costing</span>
        <Button variant="outline" size="sm" onClick={download} disabled={!!busy} data-testid="excel-template-download">{busy === "template" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}Template</Button>
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="excel-upload-btn">{busy === "preview" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}Upload sheet</Button>
        <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; pick(f); }} data-testid="excel-upload-input" />
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="excel-preview-dialog">
          <DialogHeader>
            <DialogTitle>Review Excel costing</DialogTitle>
            <DialogDescription>{preview?.filename}. Landed cost per unit (TZS) is read from the costing tab you used. It becomes the confirmed Quote Basis and the Somcore Quote applies its margin on top.</DialogDescription>
          </DialogHeader>
          {preview && (<>
            {preview.sheets?.length > 1 && (
              <label className="flex items-center gap-2 text-xs text-slate-600">Costing tab used
                <select className="h-8 rounded-md border border-border bg-white px-2 text-xs" value={preview.sheet} onChange={(e) => pick(preview.file, e.target.value)} data-testid="excel-sheet-select">
                  {preview.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            )}
            {preview.matched.length === 0 && preview.blank > 0 && <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800" data-testid="excel-no-values">No calculated landed costs were found on this tab. Open the sheet in Excel, fill it in and save it before uploading, or pick the tab you used.</p>}
            <div className="flex flex-wrap gap-2 text-xs" data-testid="excel-preview-summary">
              <span className="rounded-full bg-[#E4EEE8] px-2.5 py-1 font-semibold text-[#2F6B45]">{preview.matched.length} of {preview.total_items} items updated</span>
              {preview.blank > 0 && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">{preview.blank} left blank, current cost kept</span>}
              {preview.unmatched.length > 0 && <span className="rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-700" data-testid="excel-unmatched">{preview.unmatched.length} row(s) not matched, ignored</span>}
              {preview.errors.length > 0 && <span className="rounded-full bg-red-50 px-2.5 py-1 font-semibold text-red-700" data-testid="excel-errors">{preview.errors.length} invalid amount(s)</span>}
            </div>
            <PreviewTable p={preview} />
            {preview.errors.length > 0 && <p className="flex items-center gap-1.5 text-xs text-red-700"><AlertTriangle className="h-3.5 w-3.5" />Fix the invalid amounts in the sheet and upload again.</p>}
          </>)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Cancel</Button>
            <Button onClick={apply} disabled={busy === "apply" || !preview?.matched.length || preview?.errors.length > 0} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="excel-apply-btn">{busy === "apply" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Apply as Quote Basis</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
